import { Box, Flex, Image, FlexProps } from '@everlywell/ui-kit';
import { GatsbyImage } from 'gatsby-plugin-image';
import React from 'react';
import { ImageType } from 'utils/types';

export type ShapeBackgroundProps = FlexProps & {
  mobileImage?: ImageType | null;
  desktopImage?: ImageType | null;
  backgroundColor?: string | null;
  cardVariant?: 'vertical' | 'horizontal';
  children: React.ReactNode;
};

const ShapeBackground = React.forwardRef<HTMLDivElement, ShapeBackgroundProps>(
  (
    {
      mobileImage,
      desktopImage,
      backgroundColor,
      children,
      cardVariant = 'horizontal',
      ...flexProps
    },
    ref,
  ) => {
    const isVertical = cardVariant === 'vertical';

    return (
      <Flex
        ref={ref}
        display="flex"
        flex="1"
        justifyContent="space-between"
        flexDirection={['column', null, isVertical ? null : 'row']}
        position="relative"
        backgroundColor={backgroundColor}
        borderRadius="lg"
        overflow="hidden"
        {...flexProps}
      >
        {mobileImage ? (
          <Box
            display={['flex', null, isVertical ? null : 'none']}
            clipPath={
              'polygon(100% 0, 100% 100%, 78% 84%, 0% 100%, 0 44%, 0% 0%)'
            }
            flex="1"
            backgroundColor="tints.lightGrey"
            height="260px"
            margin="-1px" // Added margin to fix the gap between the image and the background due to the clippath
          >
            <Image
              as={GatsbyImage}
              image={mobileImage?.gatsbyImageData}
              margin="0 auto"
              alt={mobileImage?.description || ''}
              maxHeight="inherit"
            />
          </Box>
        ) : null}

        {!isVertical ? (
          <Flex
            flexDirection="column"
            flex={isVertical ? '1' : undefined}
            width={isVertical ? [] : ['auto', null, '47%']}
            minHeight={['115px', '145px']}
            paddingTop={[null, null, 20]}
            paddingLeft="8"
            paddingRight={[8, null, isVertical ? 8 : 0]}
            paddingBottom="8"
          >
            {children}
          </Flex>
        ) : null}

        {desktopImage && !isVertical ? (
          <Box
            display={['none', null, 'flex']}
            width={isVertical ? [] : ['auto', null, '53%']}
            clipPath={'polygon(0 0, 100% 0%, 100% 100%, 0 100%, 10% 18%)'}
            margin="-1px" // Added margin to fix the gap between the image and the background due to the clippath
          >
            <Image
              as={GatsbyImage}
              image={desktopImage?.gatsbyImageData}
              alt={mobileImage?.description || ''}
            />
          </Box>
        ) : null}

        {desktopImage && isVertical ? (
          <Box
            display={['flex', null, isVertical ? null : 'none']}
            clipPath={
              'polygon(100% 0, 100% 100%, 78% 84%, 0% 100%, 0 44%, 0% 0%)'
            }
            margin="-1px" // Added margin to fix the gap between the image and the background due to the clippath
            height="350px"
          >
            <Image
              as={GatsbyImage}
              image={desktopImage?.gatsbyImageData}
              margin="0 auto"
              alt={desktopImage?.description || ''}
              backgroundColor="tints.lightGrey"
              maxHeight="inherit"
            />
          </Box>
        ) : null}

        {isVertical ? (
          <Box
            padding={6}
            minHeight="255px"
            display="flex"
            flexDirection="column"
          >
            {children}
          </Box>
        ) : null}
      </Flex>
    );
  },
);

export default ShapeBackground;
